// SENTINEL: 8_Fitness - Code Renderer (Syntax, Copy)
// Vitality: Healthy
import React, { useState } from 'react';
import { Check, Copy, Terminal, Play } from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Register languages (optimization to avoid loading all)
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('css', css);

interface CodeBlockProps {
    language: string;
    code: string;
    onApply?: (code: string) => void;
    theme?: 'light' | 'dark';
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ language, code, onApply, theme = 'dark' }) => {
    const [copied, setCopied] = useState(false);
    const isDark = theme === 'dark';

    // SENTINEL: Smart Copy Logic
    const handleCopy = async () => {
        let cleanCode = code;
        cleanCode = cleanCode.replace(/^```[a-z]*\n/, '').replace(/\n```$/, '');

        await navigator.clipboard.writeText(cleanCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Style Definition based on "Antigravity" Reference
    const containerClass = isDark
        ? "my-4 rounded-xl overflow-hidden border border-gray-800 bg-[#1e1e1e] shadow-xl"
        : "my-4 rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm";

    const headerClass = isDark
        ? "flex items-center justify-between px-4 py-2 bg-[#252526] border-b border-gray-800"
        : "flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200";

    const textClass = isDark ? "text-gray-400" : "text-gray-500";
    const languageClass = isDark ? "text-gray-300" : "text-gray-700";

    const customStyle = isDark
        ? { margin: 0, padding: '1.5rem', background: 'transparent', fontSize: '0.9rem' }
        : { margin: 0, padding: '1.5rem', background: 'transparent', fontSize: '0.9rem' };

    return (
        <div className={containerClass}>
            <div className={headerClass}>
                <div className="flex items-center gap-2">
                    <Terminal size={12} className={textClass} />
                    <span className={`text-xs font-mono font-bold uppercase ${languageClass}`}>{language || 'plaintext'}</span>
                </div>
                <div className="flex items-center gap-2">
                    {onApply && (
                        <button
                            onClick={() => onApply(code)}
                            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                        >
                            <Play size={12} />
                            <span>Apply</span>
                        </button>
                    )}
                    <button
                        onClick={handleCopy}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 
                            ${copied
                                ? 'text-green-500 bg-green-500/10'
                                : isDark
                                    ? 'text-gray-400 hover:text-white hover:bg-white/10'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                        <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto text-sm font-mono leading-relaxed">
                <SyntaxHighlighter
                    language={language}
                    style={isDark ? oneDark : oneLight}
                    customStyle={customStyle}
                    wrapLongLines={true}
                    PreTag="div"
                >
                    {code}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};
